// src/components/crm/StageFormBuilder.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Button, TextInput, Breadcrumb } from "flowbite-react";
import axios from "../../config/axiosInstance";
import { STAGE_FORMS } from "../../config/apiConfig";

/* ===================== Helpers ===================== */

// Tipos com rótulos legíveis
const FIELD_TYPES = [
  { value: "text", label: "Texto curto" },
  { value: "textarea", label: "Texto longo" },
  { value: "number", label: "Número" },
  { value: "currency", label: "Moeda" },
  { value: "date", label: "Data" },
  { value: "select", label: "Lista de opções" },
  { value: "checkbox", label: "Caixa de seleção" },
  { value: "radio", label: "Opção única (radio)" },
  { value: "file", label: "Arquivo" },
  { value: "multiselect", label: "Lista de opções (múltipla)" },
];

// remove acentos e símbolos e gera camelCase
function toCamel(str = "") {
  const s = String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove acentos
    .replace(/[^a-zA-Z0-9\s_-]/g, " ") // remove símbolos
    .trim();
  if (!s) return "";
  const parts = s.split(/[\s_-]+/).filter(Boolean);
  const camel = parts
    .map((p, i) =>
      i === 0
        ? p.toLowerCase()
        : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
    )
    .join("");
  // não pode iniciar com dígito
  return camel.replace(/^[0-9]+/, "");
}

function normalizeField(f = {}) {
  return {
    name: (f.name || "").trim(),
    label: (f.label || "").trim(),
    type: f.type || "text",
    required: !!f.required,
    options: Array.isArray(f.options) ? f.options : [],
    maxLength: f.maxLength ?? undefined,
  };
}

// Gera names únicos baseados no label
function ensureUniqueNames(fields) {
  const used = new Map(); // base -> count
  return fields.map((f) => {
    let base = f.name && f.name.trim() ? f.name.trim() : toCamel(f.label);
    if (!base) base = "campo";
    let name = base;
    if (used.has(base)) {
      const n = used.get(base) + 1;
      used.set(base, n);
      name = `${base}${n}`;
    } else {
      used.set(base, 1);
    }
    return { ...f, name };
  });
}

/* ===== Editor de opções (chips) ===== */
function OptionsEditor({ value = [], onChange }) {
  const [input, setInput] = useState("");

  const commitAdd = (raw) => {
    const label = String(raw ?? input).trim();
    if (!label) return;
    const exists = (value || []).some(
      (v) => v.toLowerCase() === label.toLowerCase()
    );
    if (exists) return;
    onChange([...(value || []), label]);
    setInput("");
  };

  const removeAt = (idx) => {
    const next = [...(value || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  const move = (idx, dir) => {
    const next = [...(value || [])];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  const bulkPaste = () => {
    const raw = window.prompt(
      "Cole opções separadas por vírgula ou linha:",
      ""
    );
    if (!raw) return;
    const parts = raw
      .split(/[\n,]/g)
      .map((s) => s.trim())
      .filter(Boolean);
    const set = new Set([...(value || [])].map((v) => v.toLowerCase()));
    const merged = [...(value || [])];
    parts.forEach((p) => {
      if (!set.has(p.toLowerCase())) {
        merged.push(p);
        set.add(p.toLowerCase());
      }
    });
    onChange(merged);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitAdd();
    }
    // Backspace: remove o último se input vazio
    if (e.key === "Backspace" && !input && (value || []).length > 0) {
      removeAt((value || []).length - 1);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Digite e tecle Enter…"
          className="flex-1 h-11 border rounded-lg px-3 bg-white"
        />
        <Button size="xs" color="light" onClick={() => commitAdd()}>
          <div className="flex items-center">Adicionar</div>
        </Button>
        <Button size="xs" color="light" onClick={bulkPaste}>
          <div className="flex items-center">Importar</div>
        </Button>
      </div>

      {!value || value.length === 0 ? (
        <p className="text-xs text-gray-500">
          Nenhuma opção. Adicione pelo menos uma.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {value.map((opt, idx) => (
            <li
              key={`${opt}-${idx}`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border bg-gray-50 text-gray-700 text-sm"
            >
              <span className="truncate max-w-[200px]">{opt}</span>
              <div className="flex gap-1 ml-1">
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-700"
                  title="Mover para esquerda"
                  onClick={() => move(idx, -1)}
                >
                  ←
                </button>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-700"
                  title="Mover para direita"
                  onClick={() => move(idx, +1)}
                >
                  →
                </button>
                <button
                  type="button"
                  className="text-red-500 hover:text-red-600"
                  title="Remover"
                  onClick={() => removeAt(idx)}
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-gray-500">
        Dica: Enter adiciona, vírgula separa, Backspace remove o último quando o
        campo estiver vazio.
      </p>
    </div>
  );
}

// Toggle custom (sem Flowbite)
function Toggle({ checked, onChange, label, labelClass = "" }) {
  const handleKey = (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onChange?.(!checked);
    }
  };
  return (
    <div className="inline-flex items-center gap-2">
      {label && (
        <span className={`text-xs text-gray-600 ${labelClass}`}>{label}</span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange?.(!checked)}
        onKeyDown={handleKey}
        className={[
          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
          checked ? "bg-emerald-600" : "bg-gray-300",
        ].join(" ")}
      >
        <span
          className={[
            "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
            checked ? "translate-x-5" : "translate-x-1",
          ].join(" ")}
        />
      </button>
    </div>
  );
}

/* ===================== Page ===================== */

export default function StageFormBuilder() {
  const { stageId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: latest, isLoading } = useQuery(
    ["stage-form-latest", stageId],
    async () => {
      const res = await axios.get(STAGE_FORMS.LATEST, { params: { stageId } });
      return res?.data?.data ?? null;
    },
    { enabled: !!stageId }
  );

  const [name, setName] = useState("");
  const [version, setVersion] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [schema, setSchema] = useState([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setName(latest?.name || "Formulário da Etapa");
    setVersion(latest?.version || 1);
    setIsActive(!!latest?.isActive);
    const normalized = (latest?.schema || []).map(normalizeField);
    setSchema(ensureUniqueNames(normalized));
    setDirty(false);
  }, [latest?.id]);

  // before unload guard
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Salva (create/update)
  const upsertMut = useMutation(
    async () => {
      // sempre recalcula names a partir das labels na hora de salvar
      const prepared = ensureUniqueNames(schema.map(normalizeField));
      const payload = {
        stageId,
        name: name.trim(),
        version,
        isActive,
        schema: prepared,
      };
      if (latest?.id) {
        const res = await axios.put(STAGE_FORMS.UPDATE(latest.id), payload);
        return res.data?.data ?? res.data;
      }
      const res = await axios.post(STAGE_FORMS.CREATE, payload);
      return res.data?.data ?? res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(["stage-form-latest", stageId]);
        setDirty(false);
      },
    }
  );

  // Regras de salvamento
  const canSave = useMemo(() => {
    if (!name.trim()) return false;
    // precisa ter pelo menos um campo com label
    const hasLabel = schema.some((f) => (f.label || "").trim());
    if (!hasLabel) return false;
    // names únicos após projeção
    const projected = ensureUniqueNames(schema.map(normalizeField));
    const names = projected.map((f) => f.name);
    if (names.some((n) => !n)) return false;
    if (new Set(names).size !== names.length) return false;
    return true;
  }, [name, schema]);

  // operações de schema
  const addField = () => {
    setSchema((s) =>
      ensureUniqueNames([
        ...s,
        {
          name: "",
          label: "",
          type: "text",
          required: false,
          options: [],
          maxLength: undefined,
        },
      ])
    );
    setDirty(true);
  };

  // atualização genérica + sincronização de name a partir do label
  const updateField = (idx, patch) => {
    setSchema((prev) => {
      const next = prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
      // sempre recalcula name a partir do label atual
      const withAutoName = next.map((f) => ({
        ...f,
        name: toCamel(f.label),
      }));
      return ensureUniqueNames(withAutoName);
    });
    setDirty(true);
  };

  const removeField = (idx) => {
    setSchema((s) => ensureUniqueNames(s.filter((_, i) => i !== idx)));
    setDirty(true);
  };

  return (
    <div className="p-4 md:p-6 space-y-3">
      <Breadcrumb aria-label="Breadcrumb">
        <Breadcrumb.Item>
          <Link to="/funnels">Funis</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to="/funnels" state={{ openStageId: stageId }}>
            Etapa
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Formulário</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header */}
      <div className="bg-white p-3 flex flex-col gap-4 rounded-lg shadow-md">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              {latest?.id
                ? "Editar formulário da etapa"
                : "Novo formulário da etapa"}
            </h1>
            <p className="text-sm text-gray-500">Stage ID: {stageId}</p>
          </div>
          <div className="flex items-center gap-3">
            <Toggle
              checked={isActive}
              onChange={(v) => {
                setIsActive(v);
                setDirty(true);
              }}
              label="Ativo"
            />
            <Button
              color="gray"
              onClick={() => {
                const go = () =>
                  navigate("/funnels", {
                    state: { openStageId: stageId },
                    replace: false,
                  });
                if (!dirty) return go();
                if (window.confirm("Descartar alterações?")) go();
              }}
            >
              Voltar
            </Button>
          </div>
        </div>

        {/* Meta */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <label className="text-xs text-gray-600 block mb-1">
              Nome do formulário
            </label>
            <TextInput
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setDirty(true);
              }}
            />
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 max-w-xs">
            <label className="text-xs text-gray-600 block mb-1">Versão</label>
            <TextInput
              type="number"
              min={1}
              value={version}
              onChange={(e) => {
                setVersion(Number(e.target.value) || 1);
                setDirty(true);
              }}
            />
          </div>
        </div>

        {/* Schema Editor */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-800">Campos</h2>
            <Button size="xs" color="light" onClick={addField}>
              Adicionar campo
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-gray-500">Carregando…</p>
          ) : schema.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nenhum campo. Adicione o primeiro.
            </p>
          ) : (
            schema.map((f, idx) => (
              <div key={idx} className="rounded-md border border-gray-200 p-3 bg-white shadow-sm">
                <div className="grid md:grid-cols-12 gap-3">
                  {/* OBS: não exibimos "name" — é gerado automaticamente */}
                  <div className="md:col-span-6">
                    <label className="text-xs text-gray-600 block mb-1">
                      Label
                    </label>
                    <TextInput
                      value={f.label}
                      onChange={(e) =>
                        updateField(idx, { label: e.target.value })
                      }
                      placeholder="Ex.: Valor da proposta"
                    />
                    <p className="mt-1 text-[11px] text-gray-500">
                      Chave interna gerada:{" "}
                      <code className="text-gray-700">
                        {toCamel(f.label) || "—"}
                      </code>
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    <label className="text-xs text-gray-600 block mb-1">
                      Tipo
                    </label>
                    <select
                      className="w-full h-11 border rounded-lg px-3 bg-white"
                      value={f.type}
                      onChange={(e) =>
                        updateField(idx, { type: e.target.value })
                      }
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="text-xs text-gray-600 block mb-1">
                      Tamanho máx. (opcional)
                    </label>
                    <TextInput
                      type="number"
                      value={f.maxLength ?? ""}
                      onChange={(e) =>
                        updateField(idx, {
                          maxLength:
                            e.target.value === ""
                              ? undefined
                              : Number(e.target.value),
                        })
                      }
                      placeholder="ex.: 600"
                    />
                  </div>
                </div>

                {(f.type === "select" ||
                  f.type === "radio" ||
                  f.type === "multiselect") && (
                  <div className="mt-3">
                    <label className="text-xs text-gray-600 block mb-1">
                      Opções{" "}
                      <span className="ml-1 text-[11px] text-gray-400">
                        (
                        {f.type === "radio"
                          ? "escolha única (botões)"
                          : f.type === "multiselect"
                          ? "múltiplas escolhas"
                          : "combo (uma escolha)"}
                        )
                      </span>
                    </label>
                    <OptionsEditor
                      value={f.options || []}
                      onChange={(opts) => updateField(idx, { options: opts })}
                    />
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <Toggle
                    checked={!!f.required}
                    onChange={(v) => updateField(idx, { required: v })}
                    label="Obrigatório"
                  />
                  <Button
                    size="xs"
                    color="failure"
                    onClick={() => removeField(idx)}
                  >
                    Remover
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="flex justify-center">
          <Button
            onClick={() => upsertMut.mutate()}
            disabled={!canSave || upsertMut.isLoading}
            className="px-8 py-1"
          >
            {upsertMut.isLoading ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
